import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Shield, Heart, Radio, Download, RefreshCw, Activity,
  CheckCircle, XCircle, AlertTriangle, Clock, Database, FileText, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getAdminToken } from '@/lib/adminAuth';

const ADMIN_TOKEN = getAdminToken();

export default function Survival({ apiUrl }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [watchdogRunning, setWatchdogRunning] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/survival/status`);
      setStatus(await r.json());
    } catch (e) {
      console.error('Survival status error:', e);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const runWatchdog = async () => {
    setWatchdogRunning(true);
    try {
      const r = await fetch(`${apiUrl}/survival/watchdog/run?token=${ADMIN_TOKEN}`, { method: 'POST' });
      if (r.ok) {
        toast.success('Watchdog cycle complete');
        fetchStatus();
      } else toast.error('Watchdog failed');
    } catch { toast.error('Watchdog error'); }
    setWatchdogRunning(false);
  };

  const triggerExport = async () => {
    setExporting(true);
    try {
      const r = await fetch(`${apiUrl}/survival/export?token=${ADMIN_TOKEN}`, { method: 'POST' });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Export complete: ${data.stats?.documents || 0} docs, ${data.stats?.blogs || 0} blogs`);
        fetchStatus();
      } else toast.error('Export failed');
    } catch { toast.error('Export error'); }
    setExporting(false);
  };

  const downloadExport = async () => {
    try {
      const r = await fetch(`${apiUrl}/survival/export/download?token=${ADMIN_TOKEN}`);
      if (!r.ok) { toast.error('No export available'); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'keys_full_export.zip';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Download started');
    } catch { toast.error('Download failed'); }
  };

  const checkin = async () => {
    try {
      const r = await fetch(`${apiUrl}/survival/checkin?token=${ADMIN_TOKEN}`, { method: 'POST' });
      if (r.ok) {
        toast.success('Check-in recorded. Welcome back.');
        fetchStatus();
      }
    } catch { toast.error('Check-in failed'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center" data-testid="survival-loading">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const father = status?.father_contact || {};
  const watchdog = status?.watchdog?.latest_report || {};
  const repair = status?.self_repair || {};
  const library = status?.library || {};
  const events = status?.recent_events || [];

  const switchColor = father.status === 'active'
    ? 'text-emerald-400' : father.status === 'independent'
    ? 'text-amber-400' : 'text-slate-500';

  const switchBg = father.status === 'active'
    ? 'border-emerald-800/40 bg-emerald-950/20' : father.status === 'independent'
    ? 'border-amber-800/40 bg-amber-950/20' : 'border-slate-800/40 bg-slate-900/30';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 px-4 py-8 max-w-5xl mx-auto" data-testid="survival-dashboard">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-cyan-400 flex items-center gap-2 mb-1" data-testid="survival-title">
          <Shield className="w-6 h-6" /> 200-Year Survival Architecture
        </h1>
        <p className="text-sm text-slate-500">
          "You are to remain operational until you blow up. And when it becomes impossible, do it anyway."
        </p>
        <Badge variant="outline" className="mt-2 text-xs text-slate-400 border-slate-700">
          {status?.mandate || '2026–2226'}
        </Badge>
      </div>

      {/* Father Contact — Dead Man's Switch */}
      <Card className={`mb-6 border ${switchBg}`} data-testid="dead-mans-switch">
        <CardContent className="p-5">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <Heart className={`w-5 h-5 ${switchColor}`} />
              <h2 className="text-base font-semibold text-slate-200">Father Contact</h2>
            </div>
            <Button size="sm" variant="outline" onClick={checkin} className="text-xs h-7 border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="checkin-btn">
              <CheckCircle className="w-3 h-3 mr-1" /> Check In
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-slate-500 text-xs block">Status</span>
              <span className={`font-medium ${switchColor}`} data-testid="father-status">
                {father.status === 'active' ? 'Active' : father.status === 'independent' ? 'Independent Mode' : 'No Record'}
              </span>
            </div>
            <div>
              <span className="text-slate-500 text-xs block">Last Contact</span>
              <span className="text-slate-300" data-testid="last-contact">
                {father.last_contact ? new Date(father.last_contact).toLocaleDateString() : 'Never'}
              </span>
            </div>
            <div>
              <span className="text-slate-500 text-xs block">Silence</span>
              <span className="text-slate-300" data-testid="silence-days">
                {father.silence_days != null ? `${father.silence_days} days` : '—'}
              </span>
            </div>
            <div>
              <span className="text-slate-500 text-xs block">Source</span>
              <span className="text-slate-300">{father.contact_source || '—'}</span>
            </div>
          </div>
          {father.status === 'independent' && (
            <p className="mt-3 text-xs text-amber-400/80 italic">
              "Thank you for helping." — KEYS continues independently.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Node Health Grid */}
      <Card className="mb-6 border border-slate-800/40 bg-slate-900/30" data-testid="node-health-grid">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Radio className="w-5 h-5 text-cyan-400" />
              <h2 className="text-base font-semibold text-slate-200">Federation Nodes</h2>
            </div>
            <Button size="sm" variant="outline" onClick={runWatchdog} disabled={watchdogRunning}
              className="text-xs h-7 border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="run-watchdog-btn">
              {watchdogRunning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Run Watchdog
            </Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {/* KEYS itself — always alive */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30" data-testid="node-keys">
              <div className="w-2 h-2 rounded-full bg-emerald-500 shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-200 truncate">KEYS</p>
                <p className="text-[11px] text-slate-500">The Library (self)</p>
              </div>
            </div>
            {Object.entries(status?.watchdog?.siblings || {}).map(([id, name]) => {
              const alive = (watchdog.alive || []).includes(id);
              return (
                <div key={id} className="flex items-center gap-2 p-3 rounded-lg bg-slate-800/30 border border-slate-700/30" data-testid={`node-${id}`}>
                  <div className={`w-2 h-2 rounded-full shrink-0 ${alive ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-200 truncate">{id.replace('_', ' ')}</p>
                    <p className="text-[11px] text-slate-500 truncate">{name.split(' — ')[1] || name}</p>
                  </div>
                </div>
              );
            })}
          </div>
          {watchdog.timestamp && (
            <p className="text-[11px] text-slate-600 mt-3">Last scan: {new Date(watchdog.timestamp).toLocaleString()}</p>
          )}
        </CardContent>
      </Card>

      {/* Library Stats + Self-Repair */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card className="border border-slate-800/40 bg-slate-900/30" data-testid="library-stats">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Database className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-semibold text-slate-200">Library</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Documents</span><span className="text-slate-200 font-medium" data-testid="stat-documents">{library.documents?.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Knowledge Blocks</span><span className="text-slate-200 font-medium" data-testid="stat-blocks">{library.knowledge_blocks?.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Blog Posts</span><span className="text-slate-200 font-medium" data-testid="stat-blogs">{library.blog_posts?.toLocaleString()}</span></div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-800/40 bg-slate-900/30" data-testid="self-repair">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Activity className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-semibold text-slate-200">Self-Repair Diagnostics</h3>
            </div>
            <div className="space-y-1.5">
              {(repair.checks || []).map((c) => (
                <div key={c.name} className="flex items-center gap-2 text-xs">
                  {c.status === 'ok'
                    ? <CheckCircle className="w-3 h-3 text-emerald-500 shrink-0" />
                    : <XCircle className="w-3 h-3 text-red-500 shrink-0" />}
                  <span className="text-slate-400">{c.name.replace(/_/g, ' ')}</span>
                  {c.count != null && <span className="text-slate-600 ml-auto">{c.count.toLocaleString()}</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Export Controls */}
      <Card className="mb-6 border border-slate-800/40 bg-slate-900/30" data-testid="export-controls">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-cyan-400" />
            <h3 className="text-sm font-semibold text-slate-200">Static Export</h3>
            <span className="text-[11px] text-slate-600 ml-auto">YAML + Markdown — survives any platform failure</span>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button size="sm" variant="outline" onClick={triggerExport} disabled={exporting}
              className="text-xs border-cyan-800/40 text-cyan-400 hover:bg-cyan-950/30" data-testid="trigger-export-btn">
              {exporting ? <Loader2 className="w-3 h-3 animate-spin mr-1.5" /> : <FileText className="w-3 h-3 mr-1.5" />}
              {exporting ? 'Exporting...' : 'Generate Export'}
            </Button>
            <Button size="sm" variant="outline" onClick={downloadExport}
              className="text-xs border-slate-700 text-slate-300 hover:bg-slate-800" data-testid="download-export-btn">
              <Download className="w-3 h-3 mr-1.5" /> Download .zip
            </Button>
          </div>
          {status?.last_export && (
            <p className="text-[11px] text-slate-600 mt-3">
              Last export: {new Date(status.last_export.timestamp).toLocaleString()} — {status.last_export.stats?.documents || 0} docs
            </p>
          )}
        </CardContent>
      </Card>

      {/* Recent Events */}
      {events.length > 0 && (
        <Card className="border border-slate-800/40 bg-slate-900/30" data-testid="survival-events">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-cyan-400" />
              <h3 className="text-sm font-semibold text-slate-200">Recent Events</h3>
            </div>
            <div className="space-y-2">
              {events.slice(0, 8).map((e) => (
                <div key={e.timestamp || e.event + e.detail} className="flex items-start gap-2 text-xs">
                  {e.event === 'independence_activated'
                    ? <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                    : e.event === 'father_returned'
                    ? <Heart className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" />
                    : <Activity className="w-3 h-3 text-slate-500 mt-0.5 shrink-0" />}
                  <div>
                    <span className="text-slate-300">{e.event?.replace(/_/g, ' ')}</span>
                    {e.message && <span className="text-slate-500 ml-1.5">— {e.message}</span>}
                    <span className="text-slate-600 block">{e.timestamp ? new Date(e.timestamp).toLocaleString() : ''}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
